import React, { useEffect, useRef } from 'react';
import { X, Globe, Instagram, Facebook, Youtube, Music2 } from 'lucide-react';

// Spotify, SoundCloud and Apple Music don't have official lucide icons — use Music2 with subtle text label
const SOCIAL_LINKS = [
    { key: 'spotify_url', label: 'Spotify', Icon: Music2, color: '#1DB954' },
    { key: 'apple_music_url', label: 'Apple Music', Icon: Music2, color: '#FA243C' },
    { key: 'soundcloud_url', label: 'SoundCloud', Icon: Music2, color: '#FF5500' },
    { key: 'youtube_url', label: 'YouTube', Icon: Youtube, color: '#FF0000' },
    { key: 'instagram_url', label: 'Instagram', Icon: Instagram, color: '#E1306C' },
    { key: 'facebook_url', label: 'Facebook', Icon: Facebook, color: '#1877F2' },
    { key: 'website_url', label: 'Website', Icon: Globe, color: '#006b44' },
];

const ACCENT_RING = {
    pink: 'zz-ring-pink', mint: 'zz-ring-mint', yellow: 'zz-ring-yellow', lilac: 'zz-ring-lilac',
};
const ACCENT_BG = {
    pink: 'var(--pink-soft)', mint: 'var(--mint-soft)', yellow: 'var(--yellow-soft)', lilac: 'var(--lilac)',
};

const ArtistModal = ({ artist, onClose }) => {
    const dialogRef = useRef(null);

    useEffect(() => {
        if (!artist) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        // Focus trap — focus close button on open
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        setTimeout(() => {
            dialogRef.current?.querySelector('[data-modal-close]')?.focus();
        }, 50);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [artist, onClose]);

    if (!artist) return null;

    const accent = artist.accent || 'mint';
    const ring = ACCENT_RING[accent] || 'zz-ring-mint';
    const initials = artist.name?.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
    const photo = artist.photo_url || artist.photo || '';

    const socials = SOCIAL_LINKS.filter((s) => !!artist[s.key]);

    return (
        <div
            data-testid="artist-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="artist-modal-title"
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/60 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
        >
            <div
                ref={dialogRef}
                onClick={(e) => e.stopPropagation()}
                data-testid="artist-modal-dialog"
                className="relative w-full sm:max-w-2xl max-h-[92dvh] overflow-y-auto bg-cream border-t-4 sm:border-4 border-[var(--green)] rounded-t-[32px] sm:rounded-[32px] shadow-2xl animate-slide-up sm:animate-zoom-in"
            >
                {/* Close button */}
                <button
                    type="button"
                    data-modal-close
                    data-testid="artist-modal-close"
                    onClick={onClose}
                    aria-label="Sluit artiestinfo"
                    className="absolute top-4 right-4 sm:top-5 sm:right-5 z-10 w-11 h-11 rounded-full bg-cream border-2 border-[var(--green)] text-[var(--green-dark)] flex items-center justify-center hover:bg-[var(--green)] hover:text-cream transition-colors shadow-md"
                >
                    <X size={20} />
                </button>

                <div className="px-6 sm:px-10 pt-10 sm:pt-12 pb-8 sm:pb-10">
                    {/* Photo */}
                    <div className="flex justify-center mb-5">
                        <div className={`zz-artist-circle ${ring} w-40 sm:w-48`}>
                            {photo ? (
                                <img
                                    src={photo}
                                    alt={`Portretfoto ${artist.name}`}
                                    style={{ objectPosition: artist.ring_object_position || 'center' }}
                                />
                            ) : (
                                <div
                                    className="w-full h-full flex items-center justify-center font-display text-5xl text-[var(--green-dark)]"
                                    style={{ backgroundColor: ACCENT_BG[accent] || ACCENT_BG.mint }}
                                >
                                    {initials}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Name */}
                    <h2
                        id="artist-modal-title"
                        data-testid="artist-modal-name"
                        className="text-center font-display zz-outline-title-dark mb-3"
                        style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)' }}
                    >
                        {artist.name}
                    </h2>

                    {/* Day + location + time pills */}
                    <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                        {artist.day && (
                            <span className="zz-pill" data-testid="artist-modal-day">{artist.day}</span>
                        )}
                        {artist.location && (
                            <span className="zz-pill" data-testid="artist-modal-location">{artist.location}</span>
                        )}
                        {artist.time_slot && (
                            <span className="zz-pill" data-testid="artist-modal-time">{artist.time_slot}</span>
                        )}
                    </div>

                    {/* Bio */}
                    {artist.bio ? (
                        <p
                            data-testid="artist-modal-bio"
                            className="text-sm sm:text-base leading-relaxed text-[var(--green-dark)] max-w-prose mx-auto text-center mb-6"
                        >
                            {artist.bio}
                        </p>
                    ) : (
                        <p className="text-sm text-[var(--green-dark)]/60 italic text-center mb-6">
                            Meer info volgt binnenkort.
                        </p>
                    )}

                    {/* Socials */}
                    {socials.length > 0 && (
                        <div
                            data-testid="artist-modal-socials"
                            className="flex flex-wrap items-center justify-center gap-2.5 pt-4 border-t border-[var(--green)]/20"
                        >
                            {socials.map(({ key, label, Icon, color }) => (
                                <a
                                    key={key}
                                    href={artist[key]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`artist-modal-link-${key}`}
                                    aria-label={`${label} van ${artist.name} (opent in nieuw tabblad)`}
                                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-cream border-2 border-[var(--green)] text-[var(--green-dark)] text-xs font-semibold hover:bg-[var(--green)] hover:text-cream transition-colors"
                                    style={{ minHeight: '40px' }}
                                >
                                    <Icon size={14} style={{ color: 'currentColor' }} />
                                    {label}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ArtistModal;
